/**
 * ObjectTrackingController
 *
 * Waits for object tracking jobs to finish and makes sure the affected asset's
 * preview is rendered again afterwards.
 *
 * A job is created when a custom overlay with `trackArea` boxes is applied (see
 * the asset editor's ENSURE_OBJECT_TRACKING action). The tracking id is part of
 * the RTP checksum, so a preview is generated the moment the id is stored -
 * while the job is still running. That render cannot contain tracking data, so
 * it has to be thrown away once the job completes.
 *
 * Two things make the timing awkward:
 *
 *  - The id reaches the timeline only when the asset editor is closed
 *    (APPLY_ASSET_CHANGES). A job can easily finish before that, so the asset is
 *    tracked by its guid, which is known up front.
 *  - A finished job does not change the agd, so the checksum is unchanged and
 *    the cached render would simply be handed back. The cached model is
 *    therefore dropped by matching on the tracking id, which also covers the
 *    copy of the asset held open in the editor.
 *
 * Usage:
 *   app.objectTrackingController.watch(trackingId, assetGuid);
 *   app.objectTrackingController.resumeFromTimeline();
 */
import app from 'app';
import Marionette from 'backbone.marionette';
import storyViewBus from 'v/Story/storyViewBus';

export const TRACKING_STATUS = {
  PROCESSING: 'PROCESSING',
  DONE: 'DONE',
  FAILED: 'FAILED',
};

app.namespace.Controller.ObjectTrackingController = Marionette.Object.extend({
  initialize() {
    /** @type {Map<String, String>} tracking id -> asset guid */
    this.watching = new Map();
    /**
     * What the asset editor renders from: which assets have a job running, and
     * the last known status per tracking id.
     *
     * What matters is that both live UNDER an observable rather than being two
     * bare `Vue.observable({})` maps on a plain controller property. Nothing
     * subscribes to a bare map's Observer, so reading a key that is not there
     * yet collected no dependency and a later write reached nobody - which is
     * why the editor's badge never appeared. Reading `reactive.pendingByGuid`
     * goes through a reactive getter, so it does.
     */
    this.reactive = app.Vue.observable({
      pendingByGuid: {},
      statusById: {},
    });
  },

  /**
   * @public
   * Whether an asset is waiting on a tracking job.
   * @param assetGuid {String}
   * @return {Boolean}
   */
  isTrackingAsset(assetGuid) {
    return !!assetGuid && !!this.reactive.pendingByGuid[assetGuid];
  },

  _log(...args) {
    if (serverProfile.environment !== 'prod') {
      console.log('ObjectTrackingController:', ...args);
    }
  },

  /**
   * @private
   * @param trackingId {String}
   * @param status {String}
   */
  $_setStatus(trackingId, status) {
    this.reactive.statusById = Object.assign({}, this.reactive.statusById, {
      [trackingId]: status,
    });
  },

  /**
   * @public
   * Wait for a job to finish, then refresh the asset's preview.
   *
   * Never throws: this is a side effect of applying an overlay, which has
   * already succeeded by the time it runs.
   *
   * @param trackingId {String}
   * @param [assetGuid] {String} timeline guid of the asset being tracked
   */
  watch(trackingId, assetGuid) {
    if (!trackingId || typeof trackingId !== 'string') {
      app.doctorController.report(
        `ObjectTrackingController.watch: invalid id '${trackingId}'`
      );
      return;
    }

    if (this.watching.has(trackingId)) {
      return;
    }

    // re-applying an overlay supersedes its previous job - stop waiting for it
    if (assetGuid) {
      this.$_cancelWatchesFor(assetGuid);
    }

    this.watching.set(trackingId, assetGuid);
    this.$_setStatus(trackingId, TRACKING_STATUS.PROCESSING);
    this._log('watching', trackingId, assetGuid);
    this.$_setPendingIndication(trackingId, true);

    try {
      app.api.realTime.listenOnObjectTracking({ trackingId }).then(
        () => this.$_onDone(trackingId),
        (e) => this.$_onFailed(trackingId, e)
      );
    } catch (e) {
      this.$_onFailed(trackingId, e);
    }
  },

  /**
   * @public
   * Whether a tracking job is known to have failed - the asset editor uses this
   * to tell the user their box will not follow anything.
   * @param trackingId {String}
   * @return {Boolean}
   */
  hasFailed(trackingId) {
    return this.reactive.statusById[trackingId] === TRACKING_STATUS.FAILED;
  },

  /**
   * @public
   * Catch up after a page load: any overlay on the timeline carrying a tracking
   * id may have been left mid-flight. Ask the server where each one stands.
   *
   * A job that is already DONE is left alone - nothing records whether its
   * render predates it, and re-rendering every one on every story load would
   * cost a render per tracked overlay forever.
   *
   * @return {Promise}
   */
  resumeFromTimeline() {
    const guidByTrackingId = this.$_collectTrackedAssets();
    const trackingIds = Object.keys(guidByTrackingId);

    if (!trackingIds.length) {
      return Promise.resolve();
    }

    this._log('resuming', trackingIds);

    return Promise.all(
      trackingIds.map((trackingId) =>
        app.api.objectTracking
          .get(trackingId)
          .then(({ status }) => {
            this.$_setStatus(trackingId, status);

            if (status === TRACKING_STATUS.PROCESSING) {
              this.watch(trackingId, guidByTrackingId[trackingId]);
            } else if (
              status !== TRACKING_STATUS.DONE &&
              status !== TRACKING_STATUS.FAILED
            ) {
              app.doctorController.report(
                `ObjectTrackingController: unknown status '${status}' for ${trackingId}`
              );
            } else {
              this._log(`${trackingId} already ${status}`);
            }
          })
          .catch((e) => {
            app.doctorController.report(
              `ObjectTrackingController: status check failed for ${trackingId} - ${
                (e && e.error) || e
              }`
            );
          })
      )
    );
  },

  /**
   * @private
   * Stop waiting for any job still attached to an asset.
   * @param assetGuid {String}
   */
  $_cancelWatchesFor(assetGuid) {
    this.watching.forEach((guid, trackingId) => {
      if (guid === assetGuid) {
        this._log('superseded, dropping watch', trackingId);
        this.$_setPendingIndication(trackingId, false);
        this.watching.delete(trackingId);
        app.api.realTime.cancelListenOnObjectTracking(trackingId);
      }
    });
  },

  /**
   * @private
   * Tracking ids referenced by the timeline mapped to their asset guid, main
   * assets and collage overlays alike.
   * @return {Object<String, String>}
   */
  $_collectTrackedAssets() {
    const rv = {};

    _.each(this.$_getVisualTracks(), (visualTrack) => {
      _.each(this.$_getOverlaysOf(visualTrack), (co) => {
        if (co && co.objectTrackingId) {
          rv[co.objectTrackingId] = visualTrack.guid;
        }
      });
    });

    return rv;
  },

  /**
   * @private
   * @return {Array}
   */
  $_getVisualTracks() {
    return (app.timelineGateway && app.timelineGateway.getVisualData()) || [];
  },

  /**
   * @private
   * Custom overlays of an asset, including those on its collage tracks.
   * @param visualTrack {Object}
   * @return {Array}
   */
  $_getOverlaysOf(visualTrack) {
    if (!visualTrack) {
      return [];
    }

    const own = (visualTrack.agd && visualTrack.agd.customOverlays) || [];
    const fromOverlays = _.flatten(
      _.map(
        visualTrack.overlayTracks || [],
        (overlay) => (overlay.agd && overlay.agd.customOverlays) || []
      )
    );

    return own.concat(fromOverlays);
  },

  /**
   * @private
   * @param visualTrack {Object}
   * @param trackingId {String}
   * @return {Boolean}
   */
  carriesTrackingId(visualTrack, trackingId) {
    return _.some(
      this.$_getOverlaysOf(visualTrack),
      (co) => co && co.objectTrackingId === trackingId
    );
  },

  /**
   * @private
   * Every timeline asset holding this job, once the edit has been applied.
   *
   * Plural on purpose: duplicating an asset copies its `objectTrackingId`, so
   * more than one track can be waiting on the same job. Refreshing only the
   * first would leave the copies on a render that was just aborted.
   *
   * @param trackingId {String}
   * @return {Array}
   */
  $_findVisualTracks(trackingId) {
    return _.filter(this.$_getVisualTracks(), (visualTrack) =>
      this.carriesTrackingId(visualTrack, trackingId)
    );
  },

  /**
   * @private
   * Job finished. Drop every render made while it was running, then re-render
   * the asset if the timeline already has it.
   * @param trackingId {String}
   */
  $_onDone(trackingId) {
    const assetGuid = this.watching.get(trackingId);

    this.$_setPendingIndication(trackingId, false);
    this.watching.delete(trackingId);
    this.$_setStatus(trackingId, TRACKING_STATUS.DONE);

    // The agd is unchanged, so the checksum is too. Any cached render for this
    // job predates its tracking data and has to go - including the one the asset
    // editor made, which is keyed off its own copy of the asset. With the cache
    // clear, whoever renders next (here, or the timeline when the editor closes)
    // gets a real render.
    const dropped = app.realTimePreviewController.invalidatePreviewModelsBy(
      (visualTrack) => this.carriesTrackingId(visualTrack, trackingId)
    );

    const visualTracks = this.$_findVisualTracks(trackingId);

    if (visualTracks.length) {
      this._log(`${trackingId} done, regenerating preview`);
      _.each(visualTracks, (visualTrack) =>
        app.timelineGateway.decideRealTimePreview({
          visualTrack,
          resetRTP: true,
          delay: 0,
        })
      );
    } else {
      // still open in the asset editor, or the asset is gone - the editor
      // handles itself through the event below
      this._log(
        `${trackingId} done, not on the timeline yet (dropped ${dropped})`
      );
    }

    // An editor open on this asset has to re-render itself: its work model is a
    // copy, so the timeline refresh above cannot reach it.
    //
    // Emitted last on purpose. The editor's handler runs synchronously inside
    // $emit and registers a preview model under this checksum; announcing it
    // before decideRealTimePreview would let that call's resetRTP abort the
    // model the editor had just created, leaving the editor on a dead render.
    storyViewBus.$emit('svbObjectTrackingDone', { trackingId, assetGuid });
  },

  /**
   * @private
   * @param trackingId {String}
   * @param [e] {Object}
   */
  $_onFailed(trackingId, e) {
    if (!this.watching.has(trackingId)) {
      return; // superseded, or already settled
    }

    this.$_setPendingIndication(trackingId, false);
    this.watching.delete(trackingId);
    this.$_setStatus(trackingId, TRACKING_STATUS.FAILED);

    app.doctorController.report(
      `ObjectTrackingController: ${trackingId} failed - ${
        (e && e.error) || 'unknown'
      }`
    );
  },

  /**
   * @private
   * Show/hide "tracking" on the asset's timeline track.
   *
   * Resolved by guid rather than by tracking id: the id does not reach the
   * timeline until the asset editor is closed, so looking it up there would
   * silently skip the indication for every freshly created job.
   *
   * @param trackingId {String}
   * @param pending {Boolean}
   */
  $_setPendingIndication(trackingId, pending) {
    const guid = this.watching.get(trackingId);

    if (!guid) {
      return;
    }

    const pendingByGuid = Object.assign({}, this.reactive.pendingByGuid);

    if (pending) {
      pendingByGuid[guid] = true;
    } else {
      delete pendingByGuid[guid];
    }
    this.reactive.pendingByGuid = pendingByGuid;

    if (app.rootStore && _.isFunction(app.rootStore.dispatch)) {
      app.rootStore.dispatch('timemachine/VISUAL_TRACKING_STATE_CHANGED', {
        guid,
        pending,
      });
    }
  },

  onBeforeDestroy() {
    this.watching.forEach((guid, trackingId) => {
      app.api.realTime.cancelListenOnObjectTracking(trackingId);
    });
    this.watching.clear();
    this.reactive.pendingByGuid = {};
    this.reactive.statusById = {};
  },
});
